import { useEffect, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';
import { exchangeCodeForTokens, consumeReturnTo } from '../../lib/auth';

export const Route = createFileRoute('/auth/callback')({
  validateSearch: z.object({
    code: z.string().optional(),
    error: z.string().optional(),
    error_description: z.string().optional(),
  }),
  component: AuthCallbackPage,
});

/**
 * Restore the pre-login destination with a HARD navigation, deliberately not
 * TanStack's `navigate({ to })`.
 *
 * `stashReturnTo` (see `__root.tsx`) stashes `location.href` — path AND query
 * string, e.g. `/financials/scenarios/<id>?step=5`. TanStack treats `to` as a
 * path *pattern*, never parsing a query string out of it, so the whole `?...`
 * was silently dropped and every route's `validateSearch` saw no params and
 * fell back to its defaults. That broke every deep link with search params
 * across the portal whenever the recipient wasn't already signed in — i.e.
 * exactly the sharing case (the scenario share link landed on wizard step 1).
 *
 * A full page load costs nothing here: this route is already a cold entry from
 * Cognito's redirect, and a hard navigation honours the stashed URL verbatim
 * without hand-parsing a query string into TanStack's typed `search` object —
 * which would have to be re-audited every time a route gains a search param.
 */
export function restoreReturnTo(returnTo: string | null): void {
  window.location.replace(returnTo || '/dashboard');
}

export function AuthCallbackPage() {
  const { code, error: cognitoError, error_description } = Route.useSearch();
  const [error, setError] = useState<string | null>(
    cognitoError ? (error_description ?? cognitoError) : null,
  );

  useEffect(() => {
    if (!code) return;
    let cancelled = false;

    (async () => {
      const ok = await exchangeCodeForTokens(code);
      if (cancelled) return;

      if (ok) {
        try {
          sessionStorage.setItem('sx_analytics_login', '1');
        } catch {
          /* storage blocked - skip the login event */
        }
        restoreReturnTo(consumeReturnTo());
      } else {
        setError('Authentication failed. Please try again.');
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center space-y-4">
          <p className="text-red-600 font-medium">{error}</p>
          <button
            className="text-sm underline text-zinc-600 hover:text-zinc-900"
            onClick={() => window.location.assign('/')}
          >
            Return to app
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="flex flex-col items-center gap-3 text-zinc-500">
        <svg
          className="animate-spin h-8 w-8 text-zinc-400"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8v8H4z"
          />
        </svg>
        <span className="text-sm">Signing you in…</span>
      </div>
    </div>
  );
}
